import path from "path"
import { Command } from "commander"
import { confirm, input } from "@inquirer/prompts"
import { z } from "zod"

import { preFlightInit } from "@/src/preflights/preflight-init"
import { promptForRegistryComponents } from "@/src/commands/add"
import { FRAMEWORKS, createProject } from "@/src/utils/create-project"
import * as ERRORS from "@/src/utils/errors"
import { colors } from "@/src/utils/colors"
import { handleError } from "@/src/utils/handle-error"
import { logger } from "@/src/utils/logger"
import { addComponents } from "@/src/utils/add-components"
import {
  getProjectConfig,
  getProjectInfo,
  ProjectInfo,
} from "@/src/utils/get-project-info"
import {
  DEFAULT_COMPONENTS,
  DEFAULT_CONTEXTS,
  DEFAULT_HOOKS,
  DEFAULT_TIPTAP_ICONS,
  DEFAULT_LIB,
  DEFAULT_STYLES,
  DEFAULT_TIPTAP_EXTENSIONS,
  DEFAULT_TIPTAP_NODES,
  DEFAULT_TIPTAP_UI,
  DEFAULT_TIPTAP_UI_PRIMITIVES,
  DEFAULT_TIPTAP_UI_UTILS,
  getConfig,
  rawConfigSchema,
  resolveConfigPaths,
  type Config,
} from "@/src/utils/get-config"
import { getRegistryIndex } from "../utils/registry"
import { ensureAuthForPaidComponents } from "../utils/auth-check"
import { type Plan } from "../utils/registry/schema"
import {
  showNotionEnvSetupMessage,
  showNotionTemplateSetupMessage,
  showStylesConfigurationMessage,
  showTableNodeSetupMessage,
} from "@/src/utils/prompt-messages"
import { Framework } from "../utils/frameworks"
import {
  createWelcomeBanner,
  logLines,
  createExplanationSection,
  clearTerminal,
} from "@/src/utils/cli-ui"
import {
  autoImportStyles,
  logAutoImportResult,
} from "@/src/utils/auto-import-styles"

export const initOptionsSchema = z.object({
  cwd: z.string(),
  components: z.array(z.string()).optional(),
  silent: z.boolean(),
  isNewProject: z.boolean(),
  srcDir: z.boolean().optional(),
  reactCompiler: z.boolean().optional(),
  framework: z
    .string()
    .optional()
    .refine((val) => !val || FRAMEWORKS[val as keyof typeof FRAMEWORKS], {
      message: "Invalid framework. Please use 'next' or 'vite'.",
    }),
})

type InitOptions = z.infer<typeof initOptionsSchema> & {
  skipPreflight?: boolean
}

/**
 * Creates a themed confirmation prompt with consistent styling
 */
const createThemedConfirm = (message: string, defaultValue: boolean = true) => {
  return confirm({
    message: colors.reset(message),
    default: defaultValue,
    theme: {
      prefix: {
        done: colors.cyan("✔"),
        idle: "?",
      },
    },
  })
}

/**
 * Initialize the CLI command
 */
export const init = new Command()
  .name("init")
  .description(
    "initialize your project and install Tiptap UI components as source code"
  )
  .argument("[components...]", "the components to add")
  .option("-f, --framework <framework>", "the framework to use. (next, vite)")
  .option(
    "-c, --cwd <cwd>",
    "the working directory. defaults to the current directory.",
    process.cwd()
  )
  .option("-s, --silent", "mute output.", false)
  .option(
    "--src-dir",
    "use the src directory when creating a new project (specific to next).",
    false
  )
  .option(
    "--react-compiler",
    "enable React Compiler when creating a new project (specific to next).",
    false
  )
  .action(async (components, opts) => {
    try {
      // Clear terminal for a fresh start
      clearTerminal()

      // Show welcome banner
      const welcomeBanner = createWelcomeBanner(
        "Tiptap UI Components",
        "Install UI components or templates as editable source code in your project"
      )
      logLines(welcomeBanner)

      // Explain what Tiptap CLI does
      const explanation = createExplanationSection(
        "What are Tiptap UI Components?",
        [
          "React UI components that help you develop an editor",
          "They install as source code (not npm packages) in your src/components/",
          "UI components are a foundation for customization rather than a fixed library",
        ]
      )
      logLines(explanation)

      // Wait for user to press Enter
      await input({
        message: colors.reset(
          `Press ${colors.cyan("Enter")} to initialize a project...`
        ),
        theme: {
          prefix: {
            done: "",
            idle: "",
          },
        },
      })

      // Add visual separation
      logger.log("")

      const options = initOptionsSchema.parse({
        cwd: path.resolve(opts.cwd),
        isNewProject: false,
        components,
        ...opts,
      })
      const result = await runInit(options)

      const totalFilesProcessed =
        (result?.filesCreated?.length || 0) +
        (result?.filesUpdated?.length || 0) +
        (result?.filesSkipped?.length || 0)

      // Show success message
      if (result && totalFilesProcessed > 0) {
        // Check all file arrays for template detection
        const allFiles = [
          ...(result.filesCreated || []),
          ...(result.filesUpdated || []),
          ...(result.filesSkipped || []),
        ]

        const containsNotion = allFiles.some((path) => path.includes("notion"))
        const containsSimpleEditor = allFiles.some(
          (path) => path.includes("simple") && path.includes("editor")
        )

        if (containsNotion) {
          // Show single-line success message
          logger.log("")
          if (result.filesCreated.length > 0) {
            logger.log(
              `${colors.green("✔")} Notion template installed - ${result.filesCreated.length} files added to ${colors.cyan("src/components/")}`
            )
          } else {
            logger.log(
              `${colors.green("✔")} Notion template already installed - ${result.filesSkipped.length} files skipped`
            )
          }
          logger.log("")

          // Show combined setup message for Notion template
          const framework = result.framework || result.projectInfo?.framework
          if (framework) {
            const autoImportResult = await autoImportStyles(
              {
                filesCreated: result.filesCreated,
                filesUpdated: result.filesUpdated,
                filesSkipped: result.filesSkipped,
              },
              result.config,
              framework as Framework
            )
            logAutoImportResult(autoImportResult)

            // Show manual instructions if auto-import failed
            if (!autoImportResult.success) {
              showNotionTemplateSetupMessage(
                framework as Framework,
                result.config
              )
            } else {
              // Show only env setup since styles were auto-imported
              showNotionEnvSetupMessage(framework as Framework)
            }
          }
        } else if (containsSimpleEditor) {
          // Show single-line success message
          logger.log("")
          if (result.filesCreated.length > 0) {
            logger.log(
              `${colors.green("✔")} Simple editor template installed - ${result.filesCreated.length} files added to ${colors.cyan("src/components/")}`
            )
          } else {
            logger.log(
              `${colors.green("✔")} Simple editor template already installed - ${result.filesSkipped.length} files skipped`
            )
          }
          logger.log("")

          // Show styles configuration as required
          const framework = result.framework || result.projectInfo?.framework
          if (framework) {
            const autoImportResult = await autoImportStyles(
              {
                filesCreated: result.filesCreated,
                filesUpdated: result.filesUpdated,
                filesSkipped: result.filesSkipped,
              },
              result.config,
              framework as Framework
            )
            logAutoImportResult(autoImportResult)

            // Show manual instructions if auto-import failed
            if (!autoImportResult.success) {
              showStylesConfigurationMessage(
                framework as Framework,
                result.config
              )
            }
          }
        } else {
          // Show single-line success message
          logger.log("")
          if (result.filesCreated.length > 0) {
            logger.log(
              `${colors.green("✔")} Components installed - ${result.filesCreated.length} files added to ${colors.cyan("src/components/")}`
            )
          } else if (result.filesSkipped.length > 0) {
            logger.log(
              `${colors.green("✔")} Components already installed - ${result.filesSkipped.length} files skipped`
            )
          }
          logger.log("")

          // Show styles configuration if components were added
          if (result?.projectInfo?.framework) {
            const autoImportResult = await autoImportStyles(
              {
                filesCreated: result.filesCreated,
                filesUpdated: result.filesUpdated,
                filesSkipped: result.filesSkipped,
              },
              result.config,
              result.projectInfo.framework
            )
            logAutoImportResult(autoImportResult)

            // Show manual instructions if auto-import failed
            if (!autoImportResult.success) {
              showStylesConfigurationMessage(
                result?.projectInfo?.framework,
                result.config
              )
            }
          }

          // Show Table Node integration guide if table-node was installed
          const containsTableNode =
            result.selectedComponents?.includes("table-node")
          if (containsTableNode) {
            showTableNodeSetupMessage()
          }
        }
      }

      logger.break()
    } catch (error) {
      logger.break()
      handleError(error)
    }
  })

/**
 * Main initialization function
 */
export async function runInit(options: InitOptions) {
  const { cwd, skipPreflight, components, silent } = options
  let updatedOptions = { ...options }
  let newProjectFramework: string | undefined

  // Standard return structure creator
  const createResult = (
    config: Config,
    projectInfo: ProjectInfo | null,
    selectedComponents: string[] = [],
    addResult?: {
      filesCreated: string[]
      filesUpdated: string[]
      filesSkipped: string[]
    }
  ) => ({
    config,
    projectInfo,
    framework: newProjectFramework,
    selectedComponents,
    filesCreated: addResult?.filesCreated || [],
    filesUpdated: addResult?.filesUpdated || [],
    filesSkipped: addResult?.filesSkipped || [],
    errors: [],
  })

  // Handle preflight checks and project creation
  let projectInfo
  if (!skipPreflight) {
    const preflight = await preFlightInit(options)
    const isMissingDirOrEmptyProject =
      preflight.errors[ERRORS.MISSING_DIR_OR_EMPTY_PROJECT]

    if (isMissingDirOrEmptyProject) {
      const { projectPath, framework } = await createProject(options)
      if (!projectPath) process.exit(0)

      updatedOptions = {
        ...updatedOptions,
        cwd: projectPath,
        isNewProject: true,
      }
      newProjectFramework = framework
      logger.log("")
    }
    projectInfo = preflight.projectInfo
  } else {
    projectInfo = await getProjectInfo(cwd)
  }

  // Handle monorepo special case
  if (newProjectFramework === "next-monorepo") {
    const monorepoWebPath = path.resolve(updatedOptions.cwd, "apps/web")
    const config = await getConfig(monorepoWebPath)
    return createResult(config, projectInfo)
  }

  // Get configuration
  const projectConfig = await getProjectConfig(updatedOptions.cwd, projectInfo)
  const config = projectConfig
    ? await promptForMinimalConfig(projectConfig)
    : await promptForConfig(await getConfig(updatedOptions.cwd))

  logger.log("")

  // Handle component selection
  let selectedComponents = components || []
  if (!selectedComponents.length) {
    const shouldAddComponents = await createThemedConfirm(
      "Would you like to add a template or UI components to your project?"
    )

    if (!shouldAddComponents) {
      const fullConfig = await resolveConfigPaths(updatedOptions.cwd, config)
      return createResult(fullConfig, projectInfo)
    }

    selectedComponents = await promptForRegistryComponents(
      { ...updatedOptions, overwrite: false },
      true // Show divider
    )

    if (!selectedComponents.length) {
      const fullConfig = await resolveConfigPaths(updatedOptions.cwd, config)
      return createResult(fullConfig, projectInfo)
    }
  }

  // Handle registry fetching and authentication for selected components
  let registryIndex
  try {
    registryIndex = await getRegistryIndex()
    if (!registryIndex) {
      throw new Error("Failed to fetch registry index.")
    }
  } catch (error) {
    // Handle 401 auth error with retry
    if (
      error instanceof Error &&
      error.message.includes("You are not authorized")
    ) {
      // Trigger auth flow with assumed paid components
      const assumedPaidComponents = selectedComponents.map((name) => ({
        name,
        plans: ["start" as Plan],
      }))

      const isAuthenticated = await ensureAuthForPaidComponents(
        assumedPaidComponents,
        updatedOptions.cwd
      )

      if (!isAuthenticated) {
        logger.error(
          "Authentication failed. Cannot proceed with paid component download."
        )
        logger.log(
          "You can try again with only free components, or authenticate first."
        )
        process.exit(1)
      }

      // Retry registry fetch after authentication
      registryIndex = await getRegistryIndex()
      if (!registryIndex) {
        throw new Error("Failed to fetch registry index after authentication.")
      }
    } else {
      throw error
    }
  }

  // Map components to details and perform final authentication check
  const selectedComponentDetails = selectedComponents.map((componentName) => {
    const componentInfo = registryIndex.find(
      (item) => item.name === componentName
    )
    return {
      name: componentName,
      plans: componentInfo?.plans || [],
    }
  })

  const isAuthenticated = await ensureAuthForPaidComponents(
    selectedComponentDetails,
    updatedOptions.cwd
  )

  if (!isAuthenticated) {
    logger.error(
      "Authentication failed. Cannot proceed with paid component download."
    )
    logger.log(
      "You can try again with only free components, or authenticate first."
    )
    process.exit(1)
  }

  // Add components and return result
  const fullConfig = await resolveConfigPaths(updatedOptions.cwd, config)
  const addComponentsResult = await addComponents(
    selectedComponents,
    fullConfig,
    {
      overwrite: false,
      silent,
      isNewProject:
        updatedOptions.isNewProject ||
        projectInfo?.framework.name === "next-app",
    }
  )

  return createResult(
    fullConfig,
    projectInfo,
    selectedComponents,
    addComponentsResult || {
      filesCreated: [],
      filesUpdated: [],
      filesSkipped: [],
    }
  )
}
/**
 * Prompt for full configuration
 */
async function promptForConfig(defaultConfig: Config | null = null) {
  logger.log("")
  logger.log(colors.cyan("Project Configuration"))
  logger.log("")

  const tsx = await createThemedConfirm(
    `Would you like to use ${colors.cyan("TypeScript")} (recommended)?`,
    defaultConfig?.tsx ?? true
  )

  const rsc = await createThemedConfirm(
    `Are you using ${colors.cyan("React Server Components")}?`,
    defaultConfig?.rsc ?? true
  )

  return rawConfigSchema.parse({
    rsc,
    tsx,
    aliases: {
      components: DEFAULT_COMPONENTS,
      contexts: DEFAULT_CONTEXTS,
      hooks: DEFAULT_HOOKS,
      tiptapIcons: DEFAULT_TIPTAP_ICONS,
      lib: DEFAULT_LIB,
      tiptapExtensions: DEFAULT_TIPTAP_EXTENSIONS,
      tiptapNodes: DEFAULT_TIPTAP_NODES,
      tiptapUi: DEFAULT_TIPTAP_UI,
      tiptapUiPrimitives: DEFAULT_TIPTAP_UI_PRIMITIVES,
      tiptapUiUtils: DEFAULT_TIPTAP_UI_UTILS,
      styles: DEFAULT_STYLES,
    },
  })
}

/**
 * Prompt for minimal configuration from existing config
 */
async function promptForMinimalConfig(defaultConfig: Config) {
  return rawConfigSchema.parse({
    rsc: defaultConfig?.rsc,
    tsx: defaultConfig?.tsx,
    aliases: defaultConfig?.aliases,
  })
}
